import { DeleteIcon } from "@chakra-ui/icons";
import {
  Button,
  Flex,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Grid,
  Heading,
  Icon,
  IconButton,
  Input,
  Spacer,
  Text,
  VStack,
} from "@chakra-ui/react";
import {
  ArrayHelpers,
  Field,
  FieldArray,
  FieldProps,
  useFormikContext,
} from "formik";
import { FormControl, InputControl } from "formik-chakra-ui";
import React, { useCallback } from "react";
import { RiDeleteBin6Line } from "react-icons/ri";
import * as OrgConfig from "src/generated/hooks/org-config/requests";
import { useOrganizationPath } from "src/hooks/useOrganizationPath";
import { formatPhoneNumberWithFallback } from "src/services/format";
import * as OrgService from "src/services/orgService";
import * as RD from "src/types/remoteData";
import { z } from "zod";

export const Form = () => {
  const formik = useFormikContext<FormikType>();
  const contacts = formik.values.contacts;
  return (
    <>
      <Heading as="h2" variant="admin">
        Contact information
      </Heading>
      <FieldArray
        name="contacts"
        render={(arrayHelpers) => (
          <VStack align="stretch" spacing={4}>
            {getPhones(contacts).map((contact, index) => {
              const contactIndex = contacts.indexOf(contact);
              const valueName = `contacts.${contactIndex}.value`;
              return (
                <ContactGrid key={contact.id}>
                  <InputControl
                    as={ContactSubgrid}
                    name={`contacts.${contactIndex}.label`}
                    label="Phone label"
                    isRequired
                    inputProps={{ placeholder: "E.g.: Call support" }}
                  />
                  <Field name={valueName}>
                    {({ field, meta }: FieldProps) => {
                      const { error, touched } = meta;
                      return (
                        <FormControl
                          as={ContactSubgrid}
                          isRequired
                          isInvalid={!!error && touched}
                          name={valueName}
                        >
                          <FormLabel htmlFor={valueName}>Phone</FormLabel>
                          <Input {...field} id={valueName} />
                          {error && (
                            <FormErrorMessage>{error}</FormErrorMessage>
                          )}
                          {!error && (
                            <FormHelperText>
                              Preview:{" "}
                              {formatPhoneNumberWithFallback(field.value)}
                            </FormHelperText>
                          )}
                        </FormControl>
                      );
                    }}
                  </Field>

                  <ContactSubgrid>
                    <IconButton
                      aria-label="Delete phone"
                      alignSelf="center"
                      icon={<Icon as={RiDeleteBin6Line} />}
                      variant="outline"
                      colorScheme="primary"
                      onClick={() => arrayHelpers.remove(contactIndex)}
                      gridRowStart="input"
                    />
                  </ContactSubgrid>
                </ContactGrid>
              );
            })}

            <Flex align="center">
              <Button
                onClick={() =>
                  arrayHelpers.push({
                    id: crypto.randomUUID(),
                    type: "phone",
                    label: "",
                    value: "",
                  })
                }
                colorScheme="gray"
                variant="outline"
                size="sm"
                alignSelf="flex-start"
              >
                Add phone
              </Button>
              <Spacer />
            </Flex>
          </VStack>
        )}
      />

      <FieldArray
        name="contacts"
        render={(arrayHelpers: ArrayHelpers<ContactWithId[]>) => (
          <VStack align="stretch" spacing={4}>
            {getEmails(contacts).map((contact, index) => {
              const contactIndex = contacts.indexOf(contact);
              return (
                <ContactGrid key={contact.id}>
                  <InputControl
                    as={ContactSubgrid}
                    name={`contacts.${contactIndex}.label`}
                    label="Email label"
                    isRequired
                    inputProps={{
                      placeholder: "E.g.: Email support",
                    }}
                  />
                  <InputControl
                    as={ContactSubgrid}
                    name={`contacts.${contactIndex}.value`}
                    label="Email address"
                    isRequired
                    inputProps={{
                      placeholder: "E.g.: email@address.com",
                    }}
                  ></InputControl>
                  <ContactSubgrid>
                    <IconButton
                      aria-label="Delete email"
                      alignSelf="center"
                      icon={<Icon as={RiDeleteBin6Line} />}
                      gridRowStart="input"
                      colorScheme="primary"
                      variant="outline"
                      onClick={() => arrayHelpers.remove(contactIndex)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </ContactSubgrid>
                </ContactGrid>
              );
            })}

            <Flex align="center">
              <Button
                onClick={() =>
                  arrayHelpers.push({
                    id: crypto.randomUUID(),
                    type: "email",
                    label: "",
                    value: "",
                  })
                }
                colorScheme="gray"
                variant="outline"
                size="sm"
                alignSelf="flex-start"
              >
                Add email
              </Button>
              <Spacer />
            </Flex>
          </VStack>
        )}
      />

      <Flex direction="column">
        <Heading as="h3" variant="admin">
          Additional resources
        </Heading>
        <Text color="blackAlpha.700" fontSize="sm">
          Optionally add links to external resources like websites, helpdesks,
          and more
        </Text>
      </Flex>
      <FieldArray
        name="contacts"
        render={(arrayHelpers) => (
          <VStack align="stretch" spacing={4}>
            {getLinks(contacts).map((contact, index) => {
              const contactIndex = contacts.indexOf(contact);
              return (
                <ContactGrid key={contact.id}>
                  <InputControl
                    as={ContactSubgrid}
                    name={`contacts.${contactIndex}.label`}
                    label="Link label"
                    isRequired
                    inputProps={{
                      placeholder: "E.g.: Official website",
                    }}
                  />
                  <InputControl
                    as={ContactSubgrid}
                    name={`contacts.${contactIndex}.value`}
                    label="Link URL"
                    isRequired
                    inputProps={{
                      placeholder: "E.g.: http://www.mywebsite.com",
                    }}
                  />
                  <ContactSubgrid>
                    <IconButton
                      aria-label="Delete link"
                      alignSelf="center"
                      colorScheme="primary"
                      icon={<Icon as={RiDeleteBin6Line} />}
                      variant="outline"
                      onClick={() => arrayHelpers.remove(contactIndex)}
                      gridRowStart="input"
                    ></IconButton>
                  </ContactSubgrid>
                </ContactGrid>
              );
            })}

            <Flex align="center">
              <Button
                onClick={() =>
                  arrayHelpers.push({
                    id: crypto.randomUUID(),
                    type: "link",
                    label: "",
                    value: "",
                  })
                }
                colorScheme="gray"
                variant="outline"
                size="sm"
                alignSelf="flex-start"
              >
                Add additional resource
              </Button>
              <Spacer />
            </Flex>
          </VStack>
        )}
      />
    </>
  );
};

type WithId = { id: string };
type PhoneContactWithId = OrgConfig.PhoneContact & WithId;
type EmailContactWithId = OrgConfig.EmailContact & WithId;
type LinkContactWithId = OrgConfig.LinkContact & WithId;

const BaseContactSchema = z.object({
  label: z.string().min(1, "Label is required"),
});

const EmailContactSchema = BaseContactSchema.extend({
  type: z.literal("email"),
  value: z.string().email("Invalid email address"),
});

const PhoneContactSchema = BaseContactSchema.extend({
  type: z.literal("phone"),
  value: z.string().min(1, "Phone is required"),
});

const LinkContactSchema = BaseContactSchema.extend({
  type: z.literal("link"),
  value: z.string().url("Invalid URL format"),
});

export const ContactSchema = z.discriminatedUnion("type", [
  EmailContactSchema,
  PhoneContactSchema,
  LinkContactSchema,
]);

const ContactWithIdSchema = ContactSchema.and(z.object({ id: z.string() }));
type ContactWithId = z.infer<typeof ContactWithIdSchema>;
export const FormikSchema = z.object({
  contacts: z.array(ContactWithIdSchema),
});

export type FormikType = z.infer<typeof FormikSchema>;

// Helper functions to get filtered contacts
const getPhones = (contacts: ContactWithId[]) =>
  contacts.filter((c): c is PhoneContactWithId => c.type === "phone");
const getEmails = (contacts: ContactWithId[]) =>
  contacts.filter((c): c is EmailContactWithId => c.type === "email");
const getLinks = (contacts: ContactWithId[]) =>
  contacts.filter((c): c is LinkContactWithId => c.type === "link");

function ContactGrid(props: { children: React.ReactNode }) {
  return (
    <Grid
      gap={4}
      gridTemplateColumns="[left] 1fr [right] 1fr [delete] 2rem"
      gridTemplateRows="[label] auto [input] auto [helper] auto"
    >
      {props.children}
    </Grid>
  );
}

function ContactSubgrid(props: { children: React.ReactNode }) {
  return (
    <Grid gridRow="span 3" gridTemplateRows="subgrid" gap={0}>
      {props.children}
    </Grid>
  );
}

export function useForm(): {
  initialValues: RD.RemoteData<Error, FormikType>;
  submit: (values: FormikType) => Promise<void>;
} {
  const { remoteData, update } =
    OrgService.useOrgConfig<OrgConfig.ContactsConfig>("Contacts");

  const orgPath = useOrganizationPath();

  const initialValues: RD.RemoteData<Error, FormikType> = React.useMemo(() => {
    return remoteData.map((config) => {
      if (config === null) {
        return { contacts: [] };
      }

      return {
        contacts: config.contacts.map((contact) => ({
          id: crypto.randomUUID(),
          ...contact,
        })),
      };
    });
  }, [remoteData]);

  const submit = useCallback(
    async (values: FormikType) => {
      if (!orgPath) {
        throw new Error("Missing organization path");
      }

      await update({
        path: orgPath,
        contacts: values.contacts,
      });
    },
    [orgPath, update]
  );

  return { initialValues, submit };
}
